import cn from 'classnames';
import styles from '../LandingPage.module.scss';
import Image from 'next/image';
import React, {useEffect, useMemo, useState} from 'react';
import { SearchBox } from '../../SearchBox/SearchBox';
import { useWindowWidth } from '@react-hook/window-size';
import AirplanemodeActiveIcon from '@mui/icons-material/AirplanemodeActive';
import { ImageWithFallback } from '../../ImageWithFallback/ImageWithFallback';
import RecommendationsList from '../../RecommendationsList/RecommendationsList';
import Head from 'next/head';
import { InView } from 'react-intersection-observer';
import { useSelector } from 'react-redux';
import dynamic from 'next/dynamic';
import KeyboardArrowLeftIcon from '../../assets/arrow-left.svg';
import { checkUserCountry } from '../../../utils/checkUserCountry';
import { OffersViews } from '../../OffersViews/OffersViews';

const CallToSignUp = dynamic(() => import('../../CallToSignUp/CallToSignUp'));
const RegionalDealsTable = dynamic(() => import('../../DealsTable/RegionalDealsTable/RegionalDealsTable'), {
  ssr: false,  // Disable server-side rendering for this component
});

export const PopularDirections = ({
  cabinClass,
  popularDirections,
  popularRoutes,
  routesPage,
  directionsPage,
  totalRoutesPages,
  totalDirectionsPages,
  recommendation,
  topImage,
  authCompleted,
  seoHTML,
}) => {
  const userStore = useSelector((state) => state.user.value);
  const [searchBoxState, setSearchBoxState] = useState(null);
  const [dealsAvailable, setDealsAvailable] = useState(false);
  const defaultOrigin = useMemo(() =>({
    origin: 'All origins',
    country: userStore?.country
  }), [userStore?.country])

  useEffect(() => {
    if (cabinClass) {
      const getCabinClassCode = (textId) => {
        switch (textId) {
          case 'business-class':
            return 'BIZ';
          case 'premium-economy-class':
            return 'FLX';
          case 'first-class':
            return 'FST';
          default:
            return 'ECO';
        }
      };
      setSearchBoxState({
        from: null,
        exactFrom: false,
        to: null,
        exactTo: false,
        tripState: 'round_trip',
        cabinClass: getCabinClassCode(cabinClass),
        dateRange: {
          departure: null,
          return: null,
        },
        departure: null,
        airlineType: 'all',
        passengers: {
          adults: 1,
        },
        multi_city: [
          {
            from: null,
            to: null,
            departure: null,
            airlineType: 'all',
          },
        ],
      });
    }
  }, [cabinClass]);

  function getDirectionItem(direction) {
    function generateTitle() {
      return `Flights
      ${direction.cityFromName ? ' from ' + direction.cityFromName : ''}
     to ${direction.cityToName}`;
    }

    return (
      <a
        href={'/flights' + direction.url}
        key={direction.url}
        className={cn(styles.card, 'relative')}
      >
        <div className={styles.recomendationContainer}>
          <div className={cn(styles.icon, 'bg-review-iconAirlines')}>
            <AirplanemodeActiveIcon
              fontSize={'small'}
              className="m-auto text-white"
            />
          </div>
          <div className="h-full flex">
            <div className={styles.imageContainer}>
              <ImageWithFallback
                src={process.env.mediaUrl + direction.image?.replace('/', '')}
                objectFit="cover"
                layout="fill"
                alt={direction.cityFromName + ' - ' + direction.cityToName + ' image'}
              />
            </div>
            <div
              className={cn(
                styles.text,
                'relative flex flex-col justify-center items-start'
              )}
            >
              <div className={cn(styles.type, 'font-libre ')}>
                {generateCabinClass().name}
              </div>
              <div className={cn(styles.title, 'font-fragment')}>
                {generateTitle()}
              </div>
            </div>
          </div>
        </div>
      </a>
    );
  }

  const generateCabinClass = () => {
    switch (cabinClass) {
      case 'premium-economy-class':
        return { name: 'Premium Economy Class', code: 'FLX' };
      case 'business-class':
        return { name: 'Business Class', code: 'BIZ' };
      case 'first-class':
        return { name: 'First Class', code: 'FST' };
      default:
        return { name: 'Economy Class', code: 'ECO' };
    }
  };

  const width = useWindowWidth();
  function getRoutesPaginationUrl() {
    const rotePrevious = +routesPage - 1;
    return +directionsPage === 0 && rotePrevious === 0
      ? `/flights/${cabinClass}`
      : `/flights/${cabinClass}?directionsPage=${+directionsPage}&routesPage=${rotePrevious}`;
  }

  function getDirectionsPaginationUrl() {
    const directionPrevious = +directionsPage - 1;
    return +routesPage === 0 && directionPrevious === 0
      ? `/flights/${cabinClass}`
      : `/flights/${cabinClass}?directionsPage=${directionPrevious}&routesPage=${+routesPage}`;
  }

  return (
    <>
      <Head>
        <meta name="robots" content="index, follow" />
        <title>Cheap {generateCabinClass().name} Flights, Tickets & Deals</title>
        <link
          rel="canonical"
          href={
            cabinClass
              ? `${process.env.apiUrl}/flights/${cabinClass}`
              : `${process.env.apiUrl}/flights`
          }
        />
        <meta
          name="description"
          content={`Save big on luxury travel! Find unbeatable deals on cheap ${generateCabinClass().name.toLowerCase()} flights and tickets. Elevate your journey with First-Class.com today!`}
        />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="first-class.com" />
        <meta
          name="twitter:title"
          content={`Cheap ${generateCabinClass().name} Flights, Tickets & Deals`}
        />
        <meta
          name="twitter:description"
          content={`Save big on luxury travel! Find unbeatable deals on cheap ${generateCabinClass().name.toLowerCase()} flights and tickets. Elevate your journey with First-Class.com today!`}
        />
        <meta name="twitter:image" content={topImage} />
        <meta
          property="og:title"
          content={`Cheap ${generateCabinClass().name} Flights, Tickets & Deals`}
        />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={topImage} />
        <meta
          property="og:description"
          content={`Save big on luxury travel! Find unbeatable deals on cheap ${generateCabinClass().name.toLowerCase()} flights and tickets. Elevate your journey with First-Class.com today!`}
        />
      </Head>
      <div className={cn(styles.landing)}>
        <div className={cn(styles.topSection)}>
          <Image
            className="vjs-tech"
            src={topImage}
            alt="Top image"
            layout={'fill'}
            objectFit={'cover'}
          />
          <h1 className={cn(styles.topSectionText, 'font-fragment', checkUserCountry(userStore?.country) && dealsAvailable ? styles.topDeals : '' )}>
            {generateCabinClass().name} Flights
          </h1>
          {userStore && userStore.country && checkUserCountry(userStore.country) && (
            <div className={cn(styles.dealsTable, 'container')}>
              <RegionalDealsTable
                country={userStore.country}
                currency={userStore.currency}
                cabinClass={generateCabinClass().code}
                cabinClassTitle={generateCabinClass().name}
                intervalSeconds={7}
                setDealsAvailable={setDealsAvailable}>  
              </RegionalDealsTable>
            </div>
          )}
        </div>

        <div className="container">
          <div className={styles.content}>
          <div
              className={cn(styles.topSectionSearch, 'container mx-auto px-4', dealsAvailable ? styles.topDeals : '')}
            >
              {width >= 1024 && (
                <SearchBox
                  type={'mainSearch'}
                  activeTab={0}
                  flightSearchValue={searchBoxState}
                  searchSource={`${generateCabinClass().name} Flights Page`}
                />
              )}

            </div>
            {width < 1024 && (
              <div className={cn(styles.contentSearch, 'mx-auto')}>
                <SearchBox
                  type={'sideSearch'}
                  activeTab={0}
                  flightSearchValue={searchBoxState}
                  searchSource={`${generateCabinClass().name} Flights Page`}
                />
              </div>
            )}
          </div>
        </div>

        <div className='container'>
          <div className={cn(styles.content, 'px-4 mt-4')}>
            <OffersViews
              defaultCabinClass={generateCabinClass().code}
              filtersShown={false}
              searchSource={`Offer on ${generateCabinClass().name} Flights Page`}
              customHeader={<h2 className='font-libre text-xl my-5 lg:my-10'>
                Latest {generateCabinClass().name} round-trip flight deals
              </h2>}
              defaultOrigin={defaultOrigin}
              defaultDestination={undefined}
              allAirlines={true}>
            </OffersViews>
            {!!popularDirections.content?.length && (
              <h2
                className={cn(
                  styles.popularDirectionsText,
                  'text-left mb-6 mt-8 lg:pl-4 px-4 lg:pr-0'
                )}
              >
                Popular destinations
              </h2>
            )}
            <div
              className={cn(styles.popularDirectionsList, 'lg:pl-8 px-4 lg:pr-0')}
            >
              {popularDirections.content.slice(0, 9).map((direction) => {
                return getDirectionItem(direction);
              })}
            </div>
          </div>
        </div>
         {userStore && !userStore.userid && (
            <InView triggerOnce>
              {({ inView, ref }) => (
                <div ref={ref} className="mt-6 mb-8">
                  {inView && (
                    <div>
                      <CallToSignUp
                        country={userStore?.country}
                        authCompleted={authCompleted}
                        sourcePage={generateCabinClass().name + ' Popular Directions'}
                      />
                    </div>
                  )}
                </div>
              )}
            </InView>
          )}
        <div className="container">    
          {/* {userStore && !userStore.userid && (
            <InView triggerOnce>
              {({ inView, ref }) => (
                <div ref={ref} className="mt-6 mb-8">
                  {inView && (
                    <div>
                      <CallToSignUp
                        country={userStore?.country}
                        authCompleted={authCompleted}
                        sourcePage={generateCabinClass().name + ' Popular Directions'}
                      />
                    </div>
                  )}
                </div>
              )}
            </InView>
          )} */}
          <div
            className={cn(
              styles.popularDirectionsList,
              'lg:pl-8 px-4 lg:pr-0 mt-4'
            )}
          >
            {popularDirections.content
              .slice(9, popularDirections.content.length)
              .map((direction) => {
                return getDirectionItem(direction);
              })}
          </div>

          {!!popularDirections.content?.length && (
            <div className={styles.popularDirectionsListPagination}>
              {!!+directionsPage && (
                <a
                  href={getDirectionsPaginationUrl()}
                  className={cn(
                    styles.popularDirectionsListPaginationItem,
                    'font-fragment'
                  )}
                >
                  <button
                    style={{
                      zIndex: 2,
                      buttonOffset: 15,
                      borderRadius: '50%',
                      textAlign: 'center',
                      border: '1.5px solid #D4A976',
                      backgroundColor: '#202020',
                      color: '#D4A976',
                      width: '50px',
                      height: '50px',
                      lineHeight: '50px',
                      marginRight: '1rem',
                      cursor: 'pointer',
                    }}
                    type="button"
                  >
                    <KeyboardArrowLeftIcon
                      fontSize={'small'}
                      style={{ marginLeft: 17 }}
                    />
                  </button>
                  Previous
                </a>
              )}
              {directionsPage < totalDirectionsPages - 1 && (
                <a
                  href={`/flights/${cabinClass}?directionsPage=${
                    +directionsPage + 1
                  }&routesPage=${+routesPage}`}
                  className={cn(
                    styles.popularDirectionsListPaginationItem,
                    'font-fragment ml-auto'
                  )}
                >
                  Next
                  <button
                    style={{
                      zIndex: 2,
                      buttonOffset: 15,
                      borderRadius: '50%',
                      textAlign: 'center',
                      border: '1.5px solid #D4A976',
                      backgroundColor: '#202020',
                      color: '#D4A976',
                      width: '50px',
                      height: '50px',
                      lineHeight: '50px',
                      marginLeft: '1rem',
                      cursor: 'pointer',
                      transform: 'rotate(180deg)',
                    }}
                    type="button"
                  >
                    <KeyboardArrowLeftIcon
                      fontSize={'small'}
                      style={{ marginLeft: 17 }}
                    />
                  </button>
                </a>
              )}
            </div>
          )}
          {!!popularRoutes.content?.length && (
            <h2
              className={cn(
                styles.popularDirectionsText,
                'text-left mb-6 mt-8 lg:pl-4 px-4 lg:pr-0'
              )}
            >
              Popular routes
            </h2>
          )}
          <div
            className={cn(styles.popularDirectionsList, 'lg:pl-8 px-4 lg:pr-0')}
          >
            {popularRoutes.content.map((destination) => {
              return getDirectionItem(destination);
            })}
          </div>
          {!!popularRoutes.content?.length && (
            <div className={styles.popularDirectionsListPagination}>
              {!!+routesPage && (
                <a
                  href={getRoutesPaginationUrl()}
                  className={cn(
                    styles.popularDirectionsListPaginationItem,
                    'font-fragment'
                  )}
                >
                  <button
                    style={{
                      zIndex: 2,
                      buttonOffset: 15,
                      borderRadius: '50%',
                      textAlign: 'center',
                      border: '1.5px solid #D4A976',
                      backgroundColor: '#202020',
                      color: '#D4A976',
                      width: '50px',
                      height: '50px',
                      lineHeight: '50px',
                      marginRight: '1rem',
                      cursor: 'pointer',
                    }}
                    type="button"
                  >
                    <KeyboardArrowLeftIcon
                      fontSize={'small'}
                      style={{ marginLeft: 17 }}
                    />
                  </button>
                  Previous
                </a>
              )}
              {routesPage < totalRoutesPages - 1 && (
                <a
                  href={`/flights/${cabinClass}?directionsPage=${+directionsPage}&routesPage=${
                    +routesPage + 1
                  }`}
                  className={cn(
                    styles.popularDirectionsListPaginationItem,
                    'font-fragment ml-auto'
                  )}
                >
                  Next
                  <button
                    style={{
                      zIndex: 2,
                      buttonOffset: 15,
                      borderRadius: '50%',
                      textAlign: 'center',
                      border: '1.5px solid #D4A976',
                      backgroundColor: '#202020',
                      color: '#D4A976',
                      width: '50px',
                      height: '50px',
                      lineHeight: '50px',
                      marginLeft: '1rem',
                      cursor: 'pointer',
                      transform: 'rotate(180deg)',
                    }}
                    type="button"
                  >
                    <KeyboardArrowLeftIcon
                      fontSize={'small'}
                      style={{ marginLeft: 17 }}
                    />
                  </button>
                </a>
              )}
            </div>
          )}
        </div>
        {!routesPage && !directionsPage && (
          <div className={styles.popularDirectionsSeoHTML}>{seoHTML}</div>
        )}

        <div className="container flex justify-center items-center sm:w-full lg:mt-10 2xl:mt-20">
          <RecommendationsList items={recommendation} />
        </div>
      </div>
    </>
  );
};
